/**
 * CandidateTP + BucketAccordion + ActivitySlider + TPProgressBar Components
 * Step 6F Green Phase
 */

import React, { useState } from "react";
import { PageSpinner } from "../PageSpinner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { Info, Lightbulb, Loader2 } from "lucide-react";
import type { Activity, Bucket } from "../../types";

// TPProgressBar
export interface TPProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  total: number;
}

export function TPProgressBar({ total, ...props }: TPProgressBarProps) {
  const capped = Math.min(total, 100);
  const color = total === 100 ? "bg-green-600" : total > 100 ? "bg-brand-error" : "bg-blue-500";

  return (
    <div
      role="progressbar"
      aria-valuenow={total}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Time Priority total: ${total} of 100 points`}
      className="w-full h-2 bg-gray-200 rounded-full overflow-hidden"
      {...props}
    >
      <div
        className={`h-full rounded-full transition-all ${color}`}
        style={{ width: `${Math.min(capped, 100)}%` }}
        data-total={total === 100 ? "100" : undefined}
        data-over-limit={total > 100 ? "true" : undefined}
      />
    </div>
  );
}

// ActivitySlider
// Note: onChange signature is (activityId: string, value: number) to match test expectations
export interface ActivitySliderProps {
  activity: Activity;
  value: number;
  onChange: (activityId: string, value: number) => void;
  disabled?: boolean;
}

export function ActivitySlider({ activity, value, onChange, disabled }: ActivitySliderProps) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 min-w-0 flex-1">
        <label htmlFor={`activity-points-${activity.id}`} className="text-sm text-gray-700 min-w-0 truncate">
          {activity.name}
        </label>
        {activity.description && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`About ${activity.name}`}
                  className="inline-flex items-center justify-center text-brand-steel hover:text-brand-navy transition-colors shrink-0"
                >
                  <Info size={16} aria-hidden="true" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs break-words bg-brand-navy text-white">
                {activity.description}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <input
        id={`activity-points-${activity.id}`}
        type="number"
        min={0}
        max={100}
        step={1}
        value={value}
        onChange={(e) => onChange(activity.id, Math.min(100, Math.max(0, Math.trunc(Number(e.target.value) || 0))))}
        disabled={disabled}
        aria-label={`Points for ${activity.name}`}
        className="w-14 border border-brand-steel/50 bg-white rounded-lg px-2 py-1 text-sm text-center font-medium disabled:bg-brand-panel/60 disabled:cursor-not-allowed"
      />
    </div>
  );
}

// BucketAccordion
export interface BucketAccordionProps {
  bucket: Bucket;
  activities: Activity[];
  allocations: Record<string, number>;
  onAllocate: (activityId: string, value: number) => void;
  disabled?: boolean;
}

export function BucketAccordion({ bucket, activities, allocations, onAllocate, disabled }: BucketAccordionProps) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="border border-brand-steel/50 bg-white rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full flex items-center justify-between px-4 py-3 bg-brand-panel hover:bg-brand-panel/80 text-left"
        aria-expanded={expanded}
        aria-label={bucket.name}
      >
        <span className="text-sm font-medium text-gray-900">{bucket.name}</span>
        <span aria-hidden="true" className="text-brand-steel text-xs">
          {expanded ? "▲" : "▼"}
        </span>
      </button>
      {expanded && (
        <div className="px-4 py-3 space-y-3">
          {activities.map((activity) => (
            <ActivitySlider
              key={activity.id}
              activity={activity}
              value={allocations[activity.id] ?? 0}
              onChange={onAllocate}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// CandidateTP
export interface CandidateTPProps {
  activitiesByBucket: Array<{ bucket: Bucket; activities: Activity[] }>;
  allocations: Record<string, number>;
  total: number;
  onAllocate: (activityId: string, value: number) => void;
  onSubmit: () => void;
  /** isLocked disables all sliders — used when AQ has started */
  isLocked?: boolean;
  /** legacy alias kept for page-level compat */
  locked?: boolean;
  isLoading?: boolean;
  isSubmitting?: boolean;
  error?: string | null;
}

export function CandidateTP({
  activitiesByBucket,
  allocations,
  total,
  onAllocate,
  onSubmit,
  isLocked,
  locked,
  isLoading,
  isSubmitting,
  error,
}: CandidateTPProps) {
  const effectiveLocked = isLocked ?? locked ?? false;
  const canSubmit = total === 100 && !effectiveLocked;

  const bucketTotals = activitiesByBucket.map(({ activities }) =>
    activities.reduce((sum, a) => sum + (allocations[a.id] ?? 0), 0)
  );

  const handleJumpToBucket = (bucketId: string) => {
    const el = document.getElementById(`bucket-${bucketId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const bucketColors = [
    {
      bg: "bg-blue-50",
      border: "border-blue-200",
      text: "text-blue-700",
      badge: "bg-blue-100 text-blue-700 border-blue-300",
    },
    {
      bg: "bg-purple-50",
      border: "border-purple-200",
      text: "text-purple-700",
      badge: "bg-purple-100 text-purple-700 border-purple-300",
    },
    {
      bg: "bg-teal-50",
      border: "border-teal-200",
      text: "text-teal-700",
      badge: "bg-teal-100 text-teal-700 border-teal-300",
    },
    {
      bg: "bg-amber-50",
      border: "border-amber-200",
      text: "text-amber-700",
      badge: "bg-amber-100 text-amber-700 border-amber-300",
    },
  ];

  if (isLoading) {
    return <PageSpinner label="Loading activities" />;
  }

  return (
    <div>
      {/* Instruction banner */}
      <div className="bg-brand-panel rounded-lg p-4 mb-4 flex gap-3">
        <Lightbulb size={20} className="text-brand-crimson shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-sm text-gray-900">
          Allocate 100% across the leadership activities based on how you've actually spent your time over the last
          three months. Focus on your real calendar and daily tasks, not how you would ideally spend your time.
          Activities where you allocate more time will be assessed in greater depth, reflecting your active leadership
          areas. Ensure your total adds up to exactly 100%.
        </p>
      </div>

      {effectiveLocked && (
        <div role="alert" className="mb-4 p-3 bg-brand-highlight/40 border border-brand-warning/50 rounded-lg">
          <p className="text-sm text-amber-800">
            Your Time Priority allocation cannot be modified. Time Prioritization is locked after the Assessment Questionnaire begins.
          </p>
        </div>
      )}

      {error && (
        <div
          role="alert"
          aria-live="polite"
          className="mb-4 p-3 bg-red-50 border border-brand-error/40 rounded-lg text-sm text-brand-error"
        >
          {error}
        </div>
      )}

      {/* Progress bar — sticky */}
      <div className="bg-white border border-brand-steel/50 rounded-lg shadow-sm p-5 mb-4 sticky top-0 z-20">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-900">
            Total Allocation: <span aria-live="polite">{total} / 100</span>
          </span>
          <span
            className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
              total === 100
                ? "bg-green-50 text-green-700 border border-green-300"
                : total > 100
                ? "bg-red-50 text-red-600 border border-red-200"
                : "bg-brand-panel text-brand-steel"
            }`}
          >
            {total === 100 ? "Ready to submit" : total > 100 ? "Over limit" : `${100 - total} remaining`}
          </span>
        </div>
        <TPProgressBar total={total} />
        {activitiesByBucket.length > 0 && (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {activitiesByBucket.map(({ bucket }, idx) => {
              const color = bucketColors[idx % bucketColors.length];
              const bucketTotal = bucketTotals[idx];
              const isEmpty = bucketTotal === 0;
              return (
                <button
                  key={bucket.id}
                  type="button"
                  onClick={() => handleJumpToBucket(bucket.id)}
                  aria-label={`Jump to ${bucket.name}, ${bucketTotal} points allocated`}
                  className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-left transition-colors hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-crimson/30 ${color.border} ${color.bg} ${
                    isEmpty ? "border-dashed" : ""
                  }`}
                >
                  <span className={`text-xs font-medium truncate ${color.text}`}>{bucket.name}</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full border shrink-0 ${color.badge}`}>
                    {bucketTotal} pts
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* 4-quadrant bucket grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {activitiesByBucket.map(({ bucket, activities }, idx) => {
          const color = bucketColors[idx % bucketColors.length];
          const bucketTotal = bucketTotals[idx];

          return (
            <div
              key={bucket.id}
              id={`bucket-${bucket.id}`}
              className="scroll-mt-44 bg-white border border-brand-steel/50 rounded-lg shadow-sm overflow-hidden"
            >
              <div className={`flex items-center justify-between px-5 py-3 border-b ${color.border} ${color.bg}`}>
                <h2 className={`text-sm font-semibold ${color.text}`}>{bucket.name}</h2>
                <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full border ${color.badge}`}>
                  {bucketTotal} pts
                </span>
              </div>
              <div className="p-5 space-y-5">
                {activities.map((activity) => (
                  <ActivitySlider
                    key={activity.id}
                    activity={activity}
                    value={allocations[activity.id] ?? 0}
                    onChange={onAllocate}
                    disabled={effectiveLocked}
                  />
                ))}
                {activities.length === 0 && (
                  <p className="text-sm text-brand-steel text-center py-4">No activities in this bucket</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit || isSubmitting}
          className="w-full py-2.5 bg-brand-crimson text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-crimson/90 transition-colors inline-flex items-center justify-center gap-2"
        >
          {isSubmitting && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
          {effectiveLocked ? "Locked" : isSubmitting ? "Submitting..." : "Submit Time Priority"}
        </button>
      </div>
    </div>
  );
}

export default CandidateTP;
